import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import {
  BadgeCheck,
  CalendarHeart,
  ChevronDown,
  KeyRound,
  Phone,
  Plane,
  Plus,
  Save,
  Shield,
  Stamp,
  Trash2,
  WalletCards,
} from "lucide-react";
import { api, ApiError } from "../api";
import { LANDING_CARD_NOTICE, landingCardLabel } from '../../shared/landing-card';
import { todayIso } from '../utils';
import { AIRCRAFT_QUALIFICATION_PACKAGES } from "../../shared/aircraft-qualification-packages";
import { MANAGEABLE_SKILLS } from "../../shared/domain-types";
import type { MissionCatalogs, QualificationProfile, SessionPayload } from "../types";
import { numberLabel, roleLabel, skillLabel } from "../utils";
import { Alert, Button, Card, Field, PageHeader, SearchableCombobox, SelectField, TextareaField } from "../components/ui";

function showsAircraftQualifications(skills: readonly string[]) {
  return skills.includes("B1");
}

function CollapsibleProfileCard({
  sectionId,
  title,
  icon,
  summary,
  open,
  onToggle,
  className = "",
  children,
}: {
  sectionId: string;
  title: string;
  icon: ReactNode;
  summary?: string;
  open: boolean;
  onToggle: () => void;
  className?: string;
  children: ReactNode;
}) {
  const contentId = `profile-section-${sectionId}`;
  return (
    <Card className={`profile-section ${className}`}>
      <button
        type="button"
        className="profile-section__toggle"
        aria-expanded={open}
        aria-controls={contentId}
        onClick={onToggle}
      >
        <span className="profile-section__title">{icon}<span>{title}</span></span>
        <span className="profile-section__meta">
          {summary ? <small>{summary}</small> : null}
          <ChevronDown className={open ? "is-open" : ""} aria-hidden="true" />
        </span>
      </button>
      <div id={contentId} className="profile-section__content" hidden={!open}>
        {children}
      </div>
    </Card>
  );
}

export { OnboardingScreen } from "./Onboarding";

export function ProfileScreen({
  session,
  refresh,
  notify,
}: {
  session: SessionPayload;
  refresh: () => Promise<void>;
  notify: (message: string, tone?: "success" | "danger" | "info") => void;
}) {
  const user = session.user;
  const managementRole = user.role === "supervisor" || user.role === "manager";
  const [phone, setPhone] = useState(user.phone ?? "");
  const [hireDate, setHireDate] = useState(user.hireDate ?? "");
  const [annual, setAnnual] = useState(
    session.balances?.annual?.toString() ?? "",
  );
  const [excuse, setExcuse] = useState(
    session.balances?.excuse?.toString() ?? "",
  );
  const [profileLoading, setProfileLoading] = useState(false);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [skills, setSkills] = useState<string[]>(user.skills ?? []);
  const [skillLoading, setSkillLoading] = useState(false);
  const [qualifications, setQualifications] = useState<QualificationProfile>({ visas: [], aircraft: [], aircraftPackages: [] });
  const [catalogs, setCatalogs] = useState<MissionCatalogs | null>(null);
  const [qualificationLoading, setQualificationLoading] = useState(false);
  const [qualificationError, setQualificationError] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkPackageIds, setBulkPackageIds] = useState<string[]>([]);
  const [bulkRestrictionNote, setBulkRestrictionNote] = useState("");
  const [bulkError, setBulkError] = useState("");
  const [bulkLoading, setBulkLoading] = useState(false);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    personal: true,
    balances: false,
    visas: false,
    aircraft: false,
    password: false,
    summary: true,
  });
  const skillOptions = catalogs?.skills ?? MANAGEABLE_SKILLS;
  const hasB1 = showsAircraftQualifications(skills);
  const showAircraftSection = hasB1
    || qualifications.aircraftPackages.length > 0
    || qualifications.aircraft.length > 0;
  const toggleSection = (section: string) => setOpenSections((current) => ({
    ...current,
    [section]: !current[section],
  }));
  const openSection = (section: string) => setOpenSections((current) => ({
    ...current,
    [section]: true,
  }));
  useEffect(() => {
    if (user.role === "admin") return;
    Promise.all([
      api<QualificationProfile>("/api/profile/qualifications"),
      api<MissionCatalogs>("/api/missions/catalogs"),
    ]).then(([profile, options]) => {
      setQualifications(profile);
      setCatalogs(options);
    }).catch((caught) => setQualificationError(caught instanceof ApiError ? caught.message : "Uygunluk bilgileri yüklenemedi."));
  }, [user.id, user.role]);
  async function saveVisas() {
    setQualificationLoading(true);
    setQualificationError("");
    try {
      const saved = await api<QualificationProfile>("/api/profile/qualifications/visas", {
        method: "PUT",
        body: {
          landingCard: qualifications.landingCard ?? { hasLandingCard: false, landingCardExpiryDate: null },
          visas: qualifications.visas.map((visa) => ({
            countryCode: visa.countryCode,
            validUntil: visa.validUntil,
            validFrom: visa.validFrom ?? null,
            entryType: visa.entryType,
            restrictionNote: visa.restrictionNote || null,
          })),
        },
      });
      setQualifications(saved);
      notify("Vize ve Landing Card beyanlarınız güncellendi.");
    } catch (caught) {
      const message = caught instanceof ApiError ? caught.message : "Vize bilgileri kaydedilemedi.";
      setQualificationError(message);
      openSection("visas");
      notify(message, "danger");
    } finally {
      setQualificationLoading(false);
    }
  }
  function updateBulkPackages(value: string | string[]) {
    setBulkPackageIds(value as string[]);
    setBulkError("");
  }
  async function saveBulkAircraft() {
    setBulkError("");
    openSection("aircraft");
    if (!bulkPackageIds.length) return setBulkError("En az bir uçak–motor yetkinlik paketi seçin.");
    setBulkLoading(true);
    try {
      const saved = await api<QualificationProfile & {
        packageCount: number;
        createdCount: number;
        duplicateCount: number;
      }>("/api/profile/qualifications/aircraft", {
        method: "POST",
        body: {
          packageIds: bulkPackageIds,
          restrictionNote: bulkRestrictionNote.trim() || null,
        },
      });
      setQualifications(saved);
      setBulkPackageIds([]);
      setBulkRestrictionNote("");
      setBulkOpen(false);
      notify(
        `${saved.packageCount} uçak–motor yetkinliği kaydedildi${saved.duplicateCount ? `; ${saved.duplicateCount} mevcut paket atlandı` : ""}.`,
      );
    } catch (caught) {
      const message = caught instanceof ApiError ? caught.message : "Uçak/motor yetkinlikleri kaydedilemedi.";
      setBulkError(message);
      notify(message, "danger");
    } finally {
      setBulkLoading(false);
    }
  }
  async function removeAircraftPackage(packageId: string, label: string) {
    if (!window.confirm(`${label} yetkinliği profilinizden kaldırılsın mı?`)) return;
    setQualificationLoading(true);
    setQualificationError("");
    try {
      const saved = await api<QualificationProfile>(
        `/api/profile/qualifications/aircraft/${encodeURIComponent(packageId)}`,
        { method: "DELETE" },
      );
      setQualifications(saved);
      notify("Uçak–motor yetkinliği kaldırıldı.");
    } catch (caught) {
      const message = caught instanceof ApiError ? caught.message : "Uçak–motor yetkinliği kaldırılamadı.";
      setQualificationError(message);
      openSection("aircraft");
      notify(message, "danger");
    } finally {
      setQualificationLoading(false);
    }
  }
  async function saveSkills() {
    setSkillLoading(true);
    try {
      await api(`/api/personnel/${user.id}/skills`, { method: "PATCH", body: { skills } });
      if (!hasB1) setQualifications((current) => ({ ...current, aircraft: [], aircraftPackages: [] }));
      await refresh();
      notify("Skill bilgileriniz güncellendi.");
    } catch (caught) {
      notify(caught instanceof ApiError ? caught.message : "Skill bilgileri güncellenemedi.", "danger");
    } finally {
      setSkillLoading(false);
    }
  }
  async function saveProfile(event: FormEvent) {
    event.preventDefault();
    setProfileLoading(true);
    try {
      await api("/api/profile", {
        method: "PATCH",
        body: {
          phone: phone.trim() || null,
          ...(managementRole ? {} : { hireDate: hireDate || null }),
        },
      });
      await refresh();
      notify("Profil bilgileriniz güncellendi.");
    } catch (caught) {
      openSection("personal");
      notify(
        caught instanceof ApiError ? caught.message : "Profil güncellenemedi.",
        "danger",
      );
    } finally {
      setProfileLoading(false);
    }
  }
  async function saveBalances(event: FormEvent) {
    event.preventDefault();
    setBalanceLoading(true);
    try {
      await api("/api/balances", {
        method: "PATCH",
        body: {
          annualBalance: annual === "" ? null : Number(annual),
          excuseBalance: excuse === "" ? null : Number(excuse),
        },
      });
      await refresh();
      notify("Bakiyeleriniz audit kaydıyla güncellendi.");
    } catch (caught) {
      openSection("balances");
      notify(
        caught instanceof ApiError
          ? caught.message
          : "Bakiyeler güncellenemedi.",
        "danger",
      );
    } finally {
      setBalanceLoading(false);
    }
  }
  async function changePassword(event: FormEvent) {
    event.preventDefault();
    if (newPassword.length < 4)
      return notify("Yeni parola en az 4 karakter olmalıdır.", "danger");
    if (newPassword !== confirm)
      return notify("Yeni parolalar eşleşmiyor.", "danger");
    setPasswordLoading(true);
    try {
      await api("/api/auth/change-password", {
        method: "POST",
        body: { currentPassword, newPassword },
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirm("");
      notify("Parolanız değiştirildi.");
    } catch (caught) {
      openSection("password");
      notify(
        caught instanceof ApiError ? caught.message : "Parola değiştirilemedi.",
        "danger",
      );
    } finally {
      setPasswordLoading(false);
    }
  }
  const team = user.mainTeam ?? user.assignment?.mainTeam;
  const pattern = user.schedulePattern ?? user.assignment?.schedulePattern;
  return (
    <div className="page">
      <PageHeader
        eyebrow="HESABIM"
        title="Profil"
        description={managementRole
          ? "İletişim ve kişisel güvenlik bilgilerinizi yönetin."
          : "İletişim, kıdem, bakiye ve parola bilgilerinizi yönetin."}
      />
      <div className="profile-hero">
        <div className="avatar avatar--large">{user.displayName.charAt(0)}</div>
        <div>
          <h2>{user.displayName}</h2>
          <span>
            {user.loginCode} · {roleLabel(user.role)}
          </span>
          <div className="profile-badges">
            {team ? <b>{team} Ekibi</b> : null}
            {user.organizationUnit ? <b>{user.organizationUnit === "COMPOSITE" ? "Kompozit" : user.organizationUnit === "STRUCTURAL" ? "Yapısal" : user.organizationUnit === "PAINT" ? "Boya" : "R1-R2"}</b> : null}
            {pattern ? <b>{pattern}</b> : null}
            {user.managementShift ? (
              <b>{user.managementShift === "DAY_08_17" ? "08:00–17:00" : user.managementShift}</b>
            ) : null}
            {user.skills?.map((skill) => (
              <b key={skill}>{skillLabel(skill)}</b>
            ))}
          </div>
        </div>
      </div>
      <div className="profile-grid">
        <CollapsibleProfileCard
          sectionId="personal"
          title="Kişisel bilgiler"
          icon={<BadgeCheck />}
          summary={phone ? "İletişim bilgisi mevcut" : "İletişim bilgisi eksik"}
          open={Boolean(openSections.personal)}
          onToggle={() => toggleSection("personal")}
        >
          <form className="form-stack" onSubmit={saveProfile}>
            <Field label="Ad soyad" value={user.displayName} disabled />
            <Field
              label="Telefon (isteğe bağlı)"
              type="tel"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              hint="Girildiğinde personel rehberinde görünür."
            />
            {!managementRole ? <Field
              label="İşe giriş tarihi (isteğe bağlı)"
              type="date"
              value={hireDate}
              onChange={(event) => setHireDate(event.target.value)}
              hint="Yeni haklar sonraki iş yıl dönümünden itibaren otomatik eklenir."
            /> : null}
            <Button type="submit" loading={profileLoading}>
              <Save size={17} />
              Bilgileri kaydet
            </Button>
          </form>
          <SearchableCombobox
            label="Skill'ler"
            multiple
            value={skills}
            options={skillOptions.map((skill) => ({ value: skill, label: skillLabel(skill) }))}
            onChange={(value) => setSkills(value as string[])}
            placeholder="Skill ara veya seç"
          />
          <p className="muted">Skill bilgileri personel beyanı ve operasyonel takip amaçlıdır; resmî yetkilendirme veya sertifikasyon anlamına gelmez.</p>
          <Button type="button" variant="secondary" loading={skillLoading} onClick={() => void saveSkills()}>
            <Save size={17} /> Skill'leri kaydet
          </Button>
        </CollapsibleProfileCard>
        {!managementRole ? <CollapsibleProfileCard
          sectionId="balances"
          title="İzin bakiyeleri"
          icon={<WalletCards />}
          summary={`${numberLabel(session.balances?.annual)} yıllık / ${numberLabel(session.balances?.excuse)} mazeret`}
          open={Boolean(openSections.balances)}
          onToggle={() => toggleSection("balances")}
        >
          <div className="balance-current">
            <div>
              <span>Yıllık</span>
              <strong>{numberLabel(session.balances?.annual)}</strong>
            </div>
            <div>
              <span>Mazeret</span>
              <strong>{numberLabel(session.balances?.excuse)}</strong>
            </div>
          </div>
          <form className="form-stack" onSubmit={saveBalances}>
            <Field
              label="Kalan yıllık izin"
              type="number"
              step="0.25"
              value={annual}
              onChange={(event) => setAnnual(event.target.value)}
              placeholder="Bilinmiyor"
            />
            <Field
              label="Kalan mazeret izni"
              type="number"
              step="0.25"
              value={excuse}
              onChange={(event) => setExcuse(event.target.value)}
              placeholder="Bilinmiyor"
            />
            <Alert tone="warning">
              Bu değişiklik audit ve bakiye hareket geçmişine kaydedilir.
            </Alert>
            <Button type="submit" loading={balanceLoading}>
              <CalendarHeart size={17} />
              Bakiyeleri güncelle
            </Button>
          </form>
        </CollapsibleProfileCard> : null}
        {user.role !== "admin" ? <CollapsibleProfileCard
          sectionId="visas"
          title="Vize beyanları"
          icon={<Stamp />}
          summary={`${qualifications.visas.length} vize`}
          open={Boolean(openSections.visas)}
          onToggle={() => toggleSection("visas")}
          className="qualification-card"
        >
          <p className="muted">Bu bilgiler görev planlamasında uyarı üretir; resmî belge kontrolü değildir.</p>
          {qualificationError ? <Alert tone="danger">{qualificationError}</Alert> : null}
          <div className="declaration-list">
            {qualifications.visas.map((visa, index) => (
              <div className="declaration-row" key={`${visa.id ?? "new"}-${index}`}>
                <SearchableCombobox
                  label="Vize ülkesi"
                  value={visa.countryCode}
                  options={(catalogs?.countries ?? []).filter((country) => country.code !== "TR").map((country) => ({ value: country.code, label: country.name }))}
                  onChange={(value) => setQualifications((current) => ({ ...current, visas: current.visas.map((item, itemIndex) => itemIndex === index ? { ...item, countryCode: value as string } : item) }))}
                  placeholder="Ülke ara veya seç"
                  required
                />
                <Field label="Vize başlangıç tarihi (isteğe bağlı)" type="date" value={visa.validFrom ?? ""} max={visa.validUntil || undefined} onChange={(event) => setQualifications((current) => ({ ...current, visas: current.visas.map((item, itemIndex) => itemIndex === index ? { ...item, validFrom: event.target.value || null } : item) }))} />
                <Field label="Son geçerlilik" type="date" value={visa.validUntil} onChange={(event) => setQualifications((current) => ({ ...current, visas: current.visas.map((item, itemIndex) => itemIndex === index ? { ...item, validUntil: event.target.value } : item) }))} required />
                <SelectField label="Giriş türü" value={visa.entryType} onChange={(event) => setQualifications((current) => ({ ...current, visas: current.visas.map((item, itemIndex) => itemIndex === index ? { ...item, entryType: event.target.value as "single" | "multiple" } : item) }))}>
                  <option value="multiple">Çok girişli</option><option value="single">Tek girişli</option>
                </SelectField>
                <TextareaField label="Kısıtlama notu (isteğe bağlı)" value={visa.restrictionNote ?? ""} onChange={(event) => setQualifications((current) => ({ ...current, visas: current.visas.map((item, itemIndex) => itemIndex === index ? { ...item, restrictionNote: event.target.value || null } : item) }))} maxLength={500} />
                <Button type="button" variant="ghost" aria-label="Vize kaydını kaldır" onClick={() => setQualifications((current) => ({ ...current, visas: current.visas.filter((_, itemIndex) => itemIndex !== index) }))}><Trash2 size={17} /> Kaldır</Button>
              </div>
            ))}
          </div>
          <Button type="button" variant="secondary" onClick={() => setQualifications((current) => ({ ...current, visas: [...current.visas, { countryCode: "", validUntil: "", entryType: "multiple", restrictionNote: null }] }))}><Plus size={17} /> Vize ekle</Button>
          <h3>Landing Card / UMB</h3>
          <label className="checkbox-row"><input type="checkbox" checked={qualifications.landingCard?.hasLandingCard ?? false} onChange={event => setQualifications(current => ({ ...current, landingCard: { hasLandingCard: event.target.checked, landingCardExpiryDate: event.target.checked ? current.landingCard?.landingCardExpiryDate ?? null : null } }))} /> Landing Card / Uçak Mürettebatı Belgem (UMB) var</label>
          <p className="muted">Görev amaçlı yurt dışı personel planlamasında kullanılacaktır.</p>
          {qualifications.landingCard?.hasLandingCard && <Field label="Landing Card geçerlilik tarihi (isteğe bağlı)" type="date" value={qualifications.landingCard.landingCardExpiryDate ?? ''} onChange={event => setQualifications(current => ({ ...current, landingCard: { hasLandingCard: true, landingCardExpiryDate: event.target.value || null } }))} />}
          <p>{landingCardLabel(qualifications.landingCard ?? {}, todayIso())}</p>
          <p className="muted">{LANDING_CARD_NOTICE}</p>
          <Button type="button" loading={qualificationLoading} onClick={() => void saveVisas()}><Save size={17} /> Vize ve Landing Card bilgilerini kaydet</Button>
        </CollapsibleProfileCard> : null}
        {user.role !== "admin" && showAircraftSection ? <CollapsibleProfileCard
          sectionId="aircraft"
          title="Uçak ve motor beyanları"
          icon={<Plane />}
          summary={`${qualifications.aircraftPackages.length} uçak–motor yetkinliği`}
          open={Boolean(openSections.aircraft)}
          onToggle={() => toggleSection("aircraft")}
          className="qualification-card aircraft-qualification-card"
        >
          <p className="muted">Onaylı uçak–motor yetkinlik paketlerinden birden fazlasını tek seferde seçebilirsiniz.</p>
          {!hasB1 ? <Alert tone="warning">Mevcut yetkinlikleriniz görüntüleniyor. Yeni uçak–motor paketi eklemek için B1 skill kuralının sağlanması gerekir.</Alert> : null}
          {qualificationError ? <Alert tone="danger">{qualificationError}</Alert> : null}
          {qualifications.aircraftPackages.length ? <div className="aircraft-package-list">
            {qualifications.aircraftPackages.map((item) => (
              <div className="aircraft-package-row" key={item.packageId}>
                <div>
                  <strong>{item.label}</strong>
                  {item.restrictionNote ? <small>{item.restrictionNote}</small> : null}
                </div>
                <Button
                  type="button"
                  variant="danger"
                  disabled={qualificationLoading}
                  onClick={() => void removeAircraftPackage(item.packageId, item.label)}
                ><Trash2 size={17} /> Kaldır</Button>
              </div>
            ))}
          </div> : <p className="muted">Henüz uçak–motor yetkinliği eklenmedi.</p>}

          {hasB1 ? <div className="qualification-actions">
            <Button type="button" variant="secondary" onClick={() => setBulkOpen((current) => !current)}>
              <Plus size={17} /> Uçak Tipi Ekle
            </Button>
          </div> : null}

          {hasB1 && bulkOpen ? <div className="aircraft-bulk-form">
            <SearchableCombobox
              label="Uçak–motor yetkinlik paketleri"
              multiple
              value={bulkPackageIds}
              options={AIRCRAFT_QUALIFICATION_PACKAGES.map((item) => ({ value: item.id, label: item.label }))}
              onChange={updateBulkPackages}
              placeholder="Yetkinlik ara ve birden fazla seç"
              hint={`${bulkPackageIds.length} uçak–motor yetkinliği seçildi`}
            />
            <TextareaField
              label="Ortak kısıtlama notu (isteğe bağlı)"
              value={bulkRestrictionNote}
              onChange={(event) => setBulkRestrictionNote(event.target.value)}
              maxLength={500}
            />
            {bulkError ? <Alert tone="danger">{bulkError}</Alert> : null}
            <div className="modal-actions qualification-actions">
              <Button type="button" variant="ghost" onClick={() => setBulkOpen(false)} disabled={bulkLoading}>Kapat</Button>
              <Button type="button" loading={bulkLoading} onClick={() => void saveBulkAircraft()} disabled={bulkLoading}>
                <Save size={17} /> Kaydet
              </Button>
            </div>
          </div> : null}
        </CollapsibleProfileCard> : null}
        <CollapsibleProfileCard
          sectionId="password"
          title="Parola değiştir"
          icon={<KeyRound />}
          summary="Hesap güvenliği"
          open={Boolean(openSections.password)}
          onToggle={() => toggleSection("password")}
        >
          <form className="form-stack" onSubmit={changePassword}>
            <Field
              label="Mevcut parola"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              required
            />
            <Field
              label="Yeni parola"
              type="password"
              minLength={4}
              autoComplete="new-password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              required
            />
            <Field
              label="Yeni parola (tekrar)"
              type="password"
              minLength={4}
              autoComplete="new-password"
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
              required
            />
            <Button type="submit" loading={passwordLoading}>
              <Shield size={17} />
              Parolayı değiştir
            </Button>
          </form>
        </CollapsibleProfileCard>
        <CollapsibleProfileCard
          sectionId="summary"
          title="Hesap özeti"
          icon={<Phone />}
          summary={`${roleLabel(user.role)} / ${team ?? "Ekip yok"}`}
          open={Boolean(openSections.summary)}
          onToggle={() => toggleSection("summary")}
          className="profile-info-card"
        >
          <dl>
            <div>
              <dt>Sicil / giriş kodu</dt>
              <dd>{user.loginCode}</dd>
            </div>
            <div>
              <dt>Rol</dt>
              <dd>{roleLabel(user.role)}</dd>
            </div>
            <div>
              <dt>Ana ekip</dt>
              <dd>{team ?? "—"}</dd>
            </div>
            <div>
              <dt>Alt düzen</dt>
              <dd>{pattern ?? "—"}</dd>
            </div>
            {user.managementShift ? <div>
              <dt>Yönetim vardiyası</dt>
              <dd>{user.managementShift === "DAY_08_17" ? "08:00–17:00" : user.managementShift}</dd>
            </div> : null}
            <div>
              <dt>Ünvan</dt>
              <dd>{user.title ?? "—"}</dd>
            </div>
            <div>
              <dt>Hesap</dt>
              <dd>{user.active === false ? "Pasif" : "Aktif"}</dd>
            </div>
          </dl>
        </CollapsibleProfileCard>
      </div>
    </div>
  );
}
